/**
 * Filename normalisation for image uploads.
 *
 * The backend validates uploads by *filename extension* twice over: DRF's
 * `serializers.ImageField` defers to Django's `forms.ImageField`, whose
 * `validate_image_file_extension` rejects an empty extension outright
 * (`File extension "" is not allowed`), and `ImageValidationMixin` then
 * applies its own narrower allowlist. A `File` built from a `Blob` has
 * whatever name we give it — so any upload path that invents a name must
 * invent one with a valid extension, or the request 400s no matter how
 * well-formed the bytes are.
 */

/**
 * Extensions the backend's `ImageValidationMixin.allowed_extensions` accepts.
 * Keep in sync with `core/serializers/mixins/image_validation.py`.
 */
const ALLOWED_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "bmp",
  "tiff",
  "webp",
  "heic",
]);

/**
 * Image extensions we recognise well enough to *replace*. A trailing segment
 * outside this set is treated as part of the base name rather than an
 * extension, so `photo.final.v2` keeps its `.v2` instead of losing it.
 */
const REPLACEABLE_EXTENSIONS = new Set([
  ...ALLOWED_EXTENSIONS,
  "gif",
  "tif",
  "heif",
  "avif",
  "jfif",
  "jpe",
  "dib",
  "apng",
]);

/** MIME type → the extension the backend allows for it. */
const MIME_EXTENSIONS: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/tiff": "tiff",
  // The backend allowlist carries `heic` but not `heif`; both MIME spellings
  // (and their sequence variants) collapse onto it.
  "image/heic": "heic",
  "image/heif": "heic",
  "image/heic-sequence": "heic",
  "image/heif-sequence": "heic",
};

/**
 * Returns a filename safe to hand to the backend's image upload fields.
 *
 * Keeps `name` untouched when it already ends in an allowed extension.
 * Otherwise derives the extension from `type`, defaulting to `jpg` when the
 * MIME type is unknown or absent (S3 serves `application/octet-stream` for
 * objects stored without a content type — exactly what the markup re-edit
 * flow fetches).
 *
 * @param name Candidate filename; may be empty, null, or extensionless.
 * @param type MIME type of the bytes being uploaded.
 * @param fallbackBase Base name used when `name` is empty.
 */
export function uploadImageFilename(
  name: string | null | undefined,
  type: string | undefined,
  fallbackBase = "original",
): string {
  const trimmed = name?.trim() ?? "";
  const dot = trimmed.lastIndexOf(".");
  const suffix = dot > 0 ? trimmed.slice(dot + 1).toLowerCase() : "";

  if (ALLOWED_EXTENSIONS.has(suffix)) return trimmed;

  const base =
    (REPLACEABLE_EXTENSIONS.has(suffix) ? trimmed.slice(0, dot) : trimmed) ||
    fallbackBase;
  // `type` can carry parameters (`image/jpeg; charset=binary`) — match on the
  // essence only.
  const mime = (type ?? "").split(";")[0].trim().toLowerCase();
  return `${base}.${MIME_EXTENSIONS[mime] ?? "jpg"}`;
}
